import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ClipboardCheck, FileText, RefreshCw, Search, Eye, ShieldCheck, AlertTriangle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'https://api-pak.sudindikjt2.site';
const STATUS = {
  DRAFT: 'Belum dikirim', SUBMITTED: 'Menunggu Satlak', BTS: 'Perlu Perbaikan',
  SATLAK_APPROVED: 'Lolos Satlak', APPROVED: 'Selesai Sudin', DEFERRED: 'Dialihkan'
};
const safe = v => String(v ?? '').trim();
const getData = row => (row?.submission_snapshot && Object.keys(row.submission_snapshot).length
  ? row.submission_snapshot : row?.simpeg_snapshot || row?.sipak_snapshot || {});
async function api(path, opts={}) {
  const response = await fetch(`${API}/api/kp${path}`, {
    ...opts, headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
      ...(opts.body ? {'Content-Type':'application/json'} : {}), ...(opts.headers || {}) }
  });
  if (opts.blob && response.ok) return response.blob();
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.msg || `Permintaan gagal (${response.status})`);
  return data;
}
const cls = 'border border-slate-200 rounded-xl px-3 py-2 bg-white text-slate-800';

export default function KpSatlakReviewPage() {
  const [periods,setPeriods] = useState([]);
  const [periodId,setPeriodId] = useState('');
  const [rows,setRows] = useState([]);
  const [activeId,setActiveId] = useState(null);
  const [detail,setDetail] = useState(null);
  const [docs,setDocs] = useState({});
  const [dataReview,setDataReview] = useState('BELUM_DICEK');
  const [dataNote,setDataNote] = useState('');
  const [note,setNote] = useState('');
  const [search,setSearch] = useState('');
  const [loading,setLoading] = useState(false);
  const [saving,setSaving] = useState(false);
  const [message,setMessage] = useState('');
  const [error,setError] = useState('');

  useEffect(() => {
    api('/periods').then(items => {
      const arr = Array.isArray(items) ? items : [];
      setPeriods(arr);
      const p = arr.find(i => i.status === 'ACTIVE') || arr[0];
      if (p) setPeriodId(String(p.id));
    }).catch(e => setError(e.message));
  }, []);

  const loadRows = useCallback(async () => {
    if (!periodId) return;
    setLoading(true); setError('');
    try {
      const res = await api(`/submissions?period_id=${encodeURIComponent(periodId)}`);
      setRows(Array.isArray(res) ? res : []);
    } catch(e) {setError(e.message); setRows([]);}
    finally {setLoading(false);}
  }, [periodId]);
  useEffect(() => { loadRows(); setDetail(null); setActiveId(null); }, [loadRows]);

  const loadDetail = useCallback(async id => {
    setError(''); setMessage(''); setDetail(null); setActiveId(id);
    try {
      const d = await api(`/submissions/${encodeURIComponent(id)}`);
      const review = {};
      (d.documents || []).forEach(x => {review[x.document_code] = {
        status: safe(x.manual_status).toUpperCase() || 'BELUM_DICEK', note: x.reviewer_note || ''
      };});
      setDocs(review); setDataReview('BELUM_DICEK'); setDataNote(''); setNote(''); setDetail(d);
    } catch(e) {setError(e.message);}
  }, []);

  const filtered = useMemo(() => rows.filter(r => {
    const d = getData(r);
    const q = search.trim().toLowerCase();
    return !q || [d.full_name,d.nama,r.nip,d.nip,d.unit_kerja,r.status].some(x => safe(x).toLowerCase().includes(q));
  }),[rows,search]);
  const waiting = rows.filter(r => r.status === 'SUBMITTED').length;
  const selected = detail?.submission;
  const canReview = selected?.status === 'SUBMITTED';
  const required = (detail?.requirements || []).filter(r => r.required);
  const missing = required.filter(r => {
    const doc = (detail?.documents || []).find(d => d.document_code === r.code);
    return !doc || docs[r.code]?.status !== 'SESUAI';
  });

  async function preview(code) {
    // Open window synchronously, avoiding popup blocking after async fetch.
    const win = window.open('about:blank','_blank');
    if (!win) {setError('Izinkan popup browser untuk melihat PDF.'); return;}
    try {
      const blob = await api(`/submissions/${selected.id}/documents/${encodeURIComponent(code)}/preview`,{blob:true});
      const url = URL.createObjectURL(blob);
      win.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 120000);
    } catch (e) {win.close();setError(e.message);}
  }
  async function submitReview(decision) {
    if (!canReview || saving) return;
    if (decision === 'APPROVED' && (dataReview !== 'SESUAI' || missing.length)) {
      setError(`Belum dapat diteruskan: ${missing.length} dokumen wajib belum sesuai, atau data belum ditandai SESUAI.`);return;
    }
    if (decision === 'BTS' && !note.trim() && !dataNote.trim() &&
        !Object.values(docs).some(x => x.status === 'BTS' && x.note?.trim())) {
      setError('Cantumkan alasan perbaikan untuk guru.');return;
    }
    if (!window.confirm(decision === 'APPROVED'
      ? 'Teruskan usulan ini ke Sudin? Semua dokumen dan data harus sudah diperiksa.'
      : 'Kembalikan usulan ini kepada guru untuk diperbaiki?')) return;
    setSaving(true); setError(''); setMessage('');
    try {
      const response = await api(`/submissions/${selected.id}/review`, {
        method:'POST', body: JSON.stringify({decision, note, document_reviews:docs,
          data_review:{status:dataReview,note:dataNote}})
      });
      await loadRows(); await loadDetail(selected.id);
      setMessage(response.msg || 'Keputusan tersimpan.');
    } catch(e) {setError(e.message);} finally {setSaving(false);}
  }

  return <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-10 space-y-5">
    <header className="rounded-2xl bg-slate-900 text-white p-6">
      <div className="flex items-center gap-3"><ClipboardCheck size={25}/><h1 className="text-2xl font-bold">Usulan Kenaikan Pangkat</h1></div>
      <p className="text-sm text-slate-200 mt-2">Verifikasi Satlak · Hanya usulan guru dari kecamatan akun Anda. Keputusan diteruskan ke Sudin.</p>
    </header>
    {error && <div role="alert" className="border border-red-200 bg-red-50 text-red-800 p-3 rounded-xl flex gap-2"><AlertTriangle size={18}/>{error}</div>}
    {message && <div role="status" className="border border-green-200 bg-green-50 text-green-800 p-3 rounded-xl">{message}</div>}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <div className="p-4 rounded-xl border bg-white"><div className="text-sm text-slate-500">Semua usulan kecamatan</div><div className="text-2xl font-bold">{rows.length}</div></div>
      <div className="p-4 rounded-xl border bg-blue-50"><div className="text-sm text-blue-800">Menunggu Satlak</div><div className="text-2xl font-bold">{waiting}</div></div>
      <div className="p-4 rounded-xl border bg-green-50"><div className="text-sm text-green-800">Lolos Satlak</div><div className="text-2xl font-bold">{rows.filter(r=>r.status==='SATLAK_APPROVED').length}</div></div>
    </div>
    <div className="flex gap-3 flex-wrap items-center">
      <label className="text-sm font-semibold">Periode
        <select className={`block mt-1 ${cls}`} value={periodId} onChange={e => setPeriodId(e.target.value)}>
          {periods.length===0 && <option value="">Tidak ada periode</option>}
          {periods.map(p=><option value={p.id} key={p.id}>{p.name}</option>)}
        </select>
      </label>
      <label className="text-sm font-semibold flex-1 min-w-44">Cari guru
        <div className="flex items-center gap-2 mt-1"><Search size={17}/><input className={`${cls} w-full`} value={search} onChange={e=>setSearch(e.target.value)} placeholder="Nama / NIP / sekolah"/></div>
      </label>
      <button onClick={loadRows} className="border rounded-xl px-4 py-2 flex items-center gap-2 bg-white self-end"><RefreshCw size={16}/> Muat ulang</button>
    </div>
    <div className="grid lg:grid-cols-[minmax(250px,1fr)_minmax(400px,2fr)] gap-4 items-start">
      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="p-4 border-b font-bold">Daftar Usulan {loading && <Loader2 className="inline animate-spin" size={17}/>}</div>
        <div className="max-h-[620px] overflow-y-auto divide-y">
          {filtered.length===0 && <p className="p-5 text-sm text-slate-500">Belum ada usulan untuk periode/filter ini. Guru harus mengirim usulan terlebih dahulu.</p>}
          {filtered.map(r => {const d=getData(r);return <button key={r.id} onClick={()=>loadDetail(r.id)}
            className={`block w-full text-left p-4 hover:bg-blue-50 ${activeId===r.id?'bg-blue-50 border-l-4 border-blue-700':''}`}>
              <div className="font-semibold text-slate-900">{d.full_name || d.nama || r.nip}</div>
              <div className="text-xs text-slate-500 mt-1">{r.nip || d.nip} · {d.unit_kerja || '-'}</div>
              <div className="text-xs text-blue-700 mt-2 font-semibold">{STATUS[r.status]||r.status}</div>
            </button>})}
        </div>
      </div>
      <div className="rounded-2xl bg-white border border-slate-200 p-5 space-y-5">
        {!detail ? <p className="text-slate-500">Pilih usulan di kiri untuk melihat data dan dokumennya.</p> : <>
          <div><div className="text-lg font-bold">{detail.submission_data?.full_name || selected.nip}</div>
            <div className="text-sm text-slate-500">{selected.nip} · {detail.submission_data?.kecamatan || '-'} · {STATUS[selected.status] || selected.status}</div></div>
          <div className="border rounded-xl p-4 space-y-2 bg-slate-50">
            <div className="font-semibold">Pemeriksaan Data Guru / Usulan</div>
            {['golongan','jabatan','unit_kerja','jenis_kp'].map(key=><div key={key} className="text-sm flex gap-2"><span className="text-slate-500 capitalize w-28">{key.replace('_',' ')}</span><span>{safe(detail.submission_data?.[key]) || '-'}</span></div>)}
            <select className={cls} value={dataReview} disabled={!canReview||saving} onChange={e=>setDataReview(e.target.value)}>
              <option value="BELUM_DICEK">Belum diperiksa</option><option value="SESUAI">Sesuai</option><option value="BTS">Perlu perbaikan</option>
            </select>
            <textarea className={`${cls} w-full`} rows={2} disabled={!canReview||saving} value={dataNote} onChange={e=>setDataNote(e.target.value)} placeholder="Catatan pemeriksaan data (jika diperlukan)"/>
          </div>
          <div className="space-y-3"><h2 className="font-bold flex gap-2 items-center"><FileText size={18}/> Verifikasi Dokumen</h2>
            {(detail.requirements || []).map(req => {
              const doc=(detail.documents || []).find(d=>d.document_code===req.code);
              const state=docs[req.code] || {status:'BELUM_DICEK',note:''};
              return <div key={req.code} className="border rounded-xl p-3">
                <div className="flex items-start justify-between gap-2"><div className="font-semibold text-sm">{req.name} {req.required?'*':''}</div>
                  {doc?<button className="text-blue-700 text-xs flex items-center gap-1" onClick={()=>preview(req.code)}><Eye size={15}/> Lihat PDF</button>:<span className="text-xs text-rose-700">Belum ada file</span>}</div>
                {doc && <div className="mt-2 grid sm:grid-cols-[160px_1fr] gap-2">
                  <select className={cls} value={state.status} disabled={!canReview||saving} onChange={e=>setDocs(d=>({...d,[req.code]:{...state,status:e.target.value}}))}>
                    <option value="BELUM_DICEK">Belum diperiksa</option><option value="SESUAI">Sesuai</option><option value="BTS">Perlu perbaikan</option>
                  </select>
                  <input className={`${cls} w-full`} disabled={!canReview||saving} value={state.note} onChange={e=>setDocs(d=>({...d,[req.code]:{...state,note:e.target.value}}))} placeholder="Catatan untuk dokumen ini"/>
                </div>}
              </div>})}
          </div>
          {canReview?<div className="space-y-3 border-t pt-4">
            <textarea className={`${cls} w-full`} value={note} onChange={e=>setNote(e.target.value)} rows={2} disabled={saving} placeholder="Catatan keputusan untuk guru" />
            <p className="text-xs text-slate-500">Lolos Satlak membutuhkan status data SESUAI dan semua berkas wajib SESUAI. Dokumen yang kosong tidak dapat diloloskan.</p>
            <div className="flex gap-2 flex-wrap"><button disabled={saving} onClick={()=>submitReview('BTS')} className="rounded-xl bg-rose-100 text-rose-800 px-4 py-3 font-semibold">Perlu Perbaikan (BTS)</button>
              <button disabled={saving||dataReview!=='SESUAI'||missing.length>0} onClick={()=>submitReview('APPROVED')} className="rounded-xl bg-blue-700 text-white px-4 py-3 font-semibold disabled:opacity-40 flex gap-2 items-center"><ShieldCheck size={18}/> Lolos Satlak → Sudin</button></div>
          </div>:<p className="text-sm text-slate-500 border-t pt-3">Keputusan baru tersedia untuk usulan berstatus Menunggu Satlak.</p>}
        </>}
      </div>
    </div>
  </div>;
}
